import { RadioButton } from '@everlywell/leaves';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  ModalFooter,
  Button,
  Text,
  Checkbox,
  useMediaQuery,
  theme,
  DrawerContent,
  DrawerHeader,
  Drawer,
  DrawerFooter,
  DrawerBody,
  DrawerOverlay,
  DrawerCloseButton,
} from '@everlywell/ui-kit';
import { Link } from 'gatsby';
import React, { useState } from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

import * as S from './styles';

export enum OptionsValues {
  eligible = 'eligible',
  ineligible = 'ineligible',
}

type Option = {
  items: string[] | null;
  label: string;
  value: OptionsValues;
  type: 'checkbox' | 'radio';
};

export type ScreenerDataProps = {
  callToActionText: string;
  callToActionUrl: string;
  options: Option[] | null;
  title: string;
  description?: string;
  disclaimer?: string;
};

export type Props = {
  eligibleOptionCallback: (event: React.FormEvent<HTMLButtonElement>) => void;
  ineligibleOptionCallback: (event: React.FormEvent<HTMLButtonElement>) => void;
  modalOpen: boolean;
  modalOpenCallback: (open: boolean) => void;
  screenerData: ScreenerDataProps;
};

const getDrawerModal = (isMobile: boolean) => {
  if (isMobile) {
    return {
      Container: Drawer,
      Overlay: DrawerOverlay,
      Content: DrawerContent,
      Header: DrawerHeader,
      Body: DrawerBody,
      Footer: DrawerFooter,
      CloseButton: DrawerCloseButton,
    };
  }

  return {
    Container: Modal,
    Overlay: ModalOverlay,
    Content: ModalContent,
    Header: ModalHeader,
    Body: ModalBody,
    Footer: ModalFooter,
    CloseButton: ModalCloseButton,
  };
};

const PrePurchaseScreener: React.FC<Props> = (props: Props) => {
  const {
    eligibleOptionCallback,
    ineligibleOptionCallback,
    modalOpen,
    modalOpenCallback,
    screenerData,
  } = props;

  const {
    callToActionText,
    callToActionUrl,
    options,
    title,
    description,
    disclaimer,
  } = screenerData;

  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);

  const isCTADisabled =
    !!options?.length && !(selectedOption && selectedOption.value);

  const { onClose } = useDisclosure();

  const modalOpenHandler = () => {
    setSelectedOption(null);
    modalOpenCallback(false);
    onClose();
  };

  const handleTrackEvent = (option: Option | null) => {
    if (option) {
      const { label, value } = option;
      ewAnalytics.track({
        event: ANALYTICS.EVENTS.PRE_PURCHASE_SCREENER_ANSWER,
        data: {
          question: title,
          answer: label,
          value: value,
        },
      });
    }
  };

  const handleOnCTAClick = (event: React.FormEvent<HTMLButtonElement>) => {
    const { value } = selectedOption || {};

    if (value === OptionsValues.eligible || !options?.length) {
      eligibleOptionCallback(event);
    } else {
      ineligibleOptionCallback(event);
    }

    handleTrackEvent(selectedOption);
    modalOpenHandler();
  };

  const DrawerModal = getDrawerModal(isMobile);

  return modalOpen ? (
    <DrawerModal.Container
      onClose={modalOpenHandler}
      isOpen={modalOpen}
      size="xl"
      {...(isMobile ? { placement: 'bottom' } : {})}
    >
      <DrawerModal.Overlay />
      <DrawerModal.Content
        backgroundColor="tints.white"
        borderRadius={isMobile ? '20px 20px 0 0' : '20px'}
      >
        <DrawerModal.Header
          fontSize="lg"
          color="tints.black"
          fontWeight="normal"
          {...(!isMobile ? { paddingTop: 6 } : {})}
        >
          {title}
          <DrawerModal.CloseButton
            backgroundColor="tints.cream"
            {...(!isMobile
              ? { transform: 'scale(.75)' }
              : { transform: 'scale(.9)' })}
          />
        </DrawerModal.Header>
        <DrawerModal.Body color="tints.darkGrey">
          {description && (
            <Text
              as="div"
              dangerouslySetInnerHTML={{ __html: description }}
              sx={{
                '> *:not(:last-child)': {
                  marginBottom: 4,
                },
                ul: {
                  marginLeft: 6,
                },
                '[data-component="ContentBox"]': {
                  backgroundColor: 'tints.lightCream',
                  padding: 3,
                  borderRadius: 'xl',
                  '> *:not(:last-child)': {
                    marginBottom: 4,
                  },
                },
              }}
            />
          )}

          {options ? (
            <S.Body data-testid="options">
              {options.map((option: Option) => {
                const { label, value, items, type } = option;

                if (type === 'checkbox') {
                  return (
                    <Checkbox
                      onChange={(e) =>
                        setSelectedOption(e.target.checked ? option : null)
                      }
                      key={label}
                    >
                      <Text>{label}</Text>
                    </Checkbox>
                  );
                }

                return (
                  <Box
                    key={label}
                    onClick={() => setSelectedOption(option)}
                    border="1px solid"
                    borderColor="viridian.light"
                    borderRadius={0.5}
                    marginBottom={6}
                    sx={{
                      '&:hover, &:focus': {
                        borderColor: 'viridian.base',
                      },
                    }}
                    boxShadow={'base'}
                  >
                    <Flex
                      as="label"
                      alignItems="center"
                      backgroundColor={'viridian.wash'}
                      cursor="pointer"
                      display="flex"
                      justifyContent="flex-start"
                      padding={3}
                      sx={{
                        '&:hover, &:focus': {
                          backgroundColor: 'viridian.lighter',
                        },
                      }}
                    >
                      <RadioButton
                        id={label}
                        value={value}
                        checked={
                          selectedOption
                            ? selectedOption.label === label
                            : false
                        }
                        name="option"
                      />

                      <S.OptionTitle>{label}</S.OptionTitle>
                    </Flex>

                    {items ? (
                      <S.RadioOptionContent>
                        <S.UL>
                          {items.map((item: string, idx: number) => (
                            <S.LI key={idx}>{item}</S.LI>
                          ))}
                        </S.UL>
                      </S.RadioOptionContent>
                    ) : null}
                  </Box>
                );
              })}
            </S.Body>
          ) : null}

          <ModalFooter
            paddingX="0"
            display="flex"
            flexDirection="column"
            gap={4}
          >
            <Flex
              width="100%"
              paddingX="0"
              gap={4}
              sx={{
                '> a, > button': {
                  maxHeight: '44px',
                  fontSize: ['md', null, null, 'lg'],
                },
              }}
            >
              <Button
                variant="secondary"
                width="100%"
                onClick={modalOpenHandler}
              >
                Go back
              </Button>
              <Button
                data-testid="cta"
                onClick={handleOnCTAClick}
                isDisabled={isCTADisabled}
                width="100%"
                {...(callToActionUrl ? { as: Link, to: callToActionUrl } : {})}
              >
                {callToActionText}
              </Button>
            </Flex>
            <Text size="sm" color="tints.darkGrey" textAlign="center">
              {disclaimer}
            </Text>
          </ModalFooter>
        </DrawerModal.Body>
      </DrawerModal.Content>
    </DrawerModal.Container>
  ) : null;
};

export default PrePurchaseScreener;
